import React, { useState, useRef, useEffect } from 'react';
import { useQuery } from 'react-query';

import { dangerouslyFetchAllPages } from 'js-utils/http';
import InteractiveMap from 'eventbrite_design_system/interactiveMap/InteractiveMap';

import { mapOptions } from './constants';
import InteractiveMapMarker from './ProtestMapMarker';
import MapMarkerTooltip from './ProtestMapMarkerTooltip';
import {
    MapBannerDesktop,
    MapBannerMobileTablet,
    PROTEST_COLLECTION_URL,
} from './ProtestMapBanner';

import './ProtestMap.scss';

const EMPTY_STATE = { objects: [] };
let mockEventsResponse;

const US_CENTER = { lat: 37.8283, lng: -99.5795 };

const GOOGLE_MAPS_CLIENT_KEY = 'gme-eventbrite';

const ErrorStateMobile = () => (
    <div className="homepage-map__mobile-placeholder">
        <a className="eds-align--center" href={PROTEST_COLLECTION_URL}>
            Error loading events. Click here to view them all.
        </a>
    </div>
);

const ErrorStateDesktop = () => (
    <div className="homepage-map__desktop-placeholder">
        <a className="eds-align--center" href={PROTEST_COLLECTION_URL}>
            Error loading events. Click here to view them all.
        </a>
    </div>
);

class ProtestMapErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    render() {
        if (this.state.hasError) {
            return null;
        }

        return this.props.children;
    }
}

const MapBase = (props) => {
    if (props.hasError) {
        return props.errorState;
    }

    // This is here to handle the use case of having two maps at once
    // for EDS responsive classes.
    if (
        !props.parentRef.current ||
        props.parentRef.current.offsetParent === null
    ) {
        return props.refreshPlaceholder;
    }

    return (
        <InteractiveMap
            googleMapsClient={GOOGLE_MAPS_CLIENT_KEY}
            height={props.isMobile ? '340px' : '475px'}
            language="en"
            region="us"
            width={props.isMobile ? '100%' : '70%'}
            // us
            center={US_CENTER}
            // london
            // {51.5074° N, 0.1278° W}
            // center={{ lat: 51.5074, lng: 0.1278 }}
            // for prod
            //defaultZoom={4}
            // dev
            defaultZoom={props.isMobile ? 3 : 4}
            options={mapOptions}
        >
            {props.collectionsResponse.objects
                .filter(
                    (collection_event) =>
                        !!(
                            collection_event.event.primary_venue &&
                            collection_event.event.primary_venue.address &&
                            collection_event.event.primary_venue.address
                                .latitude &&
                            collection_event.event.primary_venue.address
                                .longitude
                        ),
                )
                .map((collection_event) => (
                    <InteractiveMapMarker
                        onMapMarkerClick={props.handleMapMarkerClick(
                            collection_event.event.id,
                        )}
                        key={collection_event.event.id}
                        label={collection_event.event.name}
                        lat={
                            collection_event.event.primary_venue.address
                                .latitude
                        }
                        lng={
                            collection_event.event.primary_venue.address
                                .longitude
                        }
                    />
                ))}
        </InteractiveMap>
    );
};

export default function ProtestMap(props) {
    let hasError = false;
    const desktopRef = useRef(null);
    const mobileRef = useRef(null);

    const [mapMarkerRef, setMapMarkerRef] = useState(null);
    const [selectedEventId, setSelectedEventId] = useState(null);

    const handleMapMarkerClick = (eventId) => (ref) => {
        setSelectedEventId(eventId);
        setMapMarkerRef(ref);
    };

    let collectionsResponse = EMPTY_STATE;

    const isQA = props.env.serverUrl.indexOf('evbqa') > -1;
    const [hasFixtureForQALoaded, setHasFixtureForQALoaded] = useState(false);

    if (isQA) {
        // in QA, we don't have the collection available, so use a mock instead.
        // The mock is very large so we don't want to load it for the production site.
        useEffect(() => {
            import('./fixtures').then((fixturesModule) => {
                mockEventsResponse = fixturesModule.mockEventsResponse;
                setHasFixtureForQALoaded(true);
            });
        }, []);

        if (hasFixtureForQALoaded) {
            collectionsResponse = mockEventsResponse;
        }
    }

    if (!isQA) {
        // In prod/dev: collection pid cwxbtpy
        // For dev, use the production API so we can get this collection directly
        const { status, data } = useQuery(
            'cwxbtpy',
            () =>
                dangerouslyFetchAllPages(
                    `/api/v3/destination/collections/cwxbtpy/editorial-events/?expand.destination_event=image,primary_venue`,
                    'collection_events',
                ),
            { retry: 1 },
        );

        collectionsResponse = data;

        if (status === 'error') {
            collectionsResponse = EMPTY_STATE;
            hasError = true;
        }

        if (status === 'loading') {
            collectionsResponse = EMPTY_STATE;
        }
    }

    const events = collectionsResponse.objects.map(({ event }) => {
        const eventParsedForMarker = {
            ...event,
            imageUrl: event.image ? event.image.url : undefined,
        };
        return eventParsedForMarker;
    });

    let shouldOpenDownwards = false;
    let shouldOpenRightwards = false;

    if (mapMarkerRef) {
        shouldOpenDownwards = mapMarkerRef.getBoundingClientRect().y < 270;
        shouldOpenRightwards = mapMarkerRef.getBoundingClientRect().x < 200;
    }

    useEffect(() => {
        // Moves the Google logo to the top to avoid overlapping the banner
        const css = document.createElement('style');
        css.type = 'text/css';
        css.innerHTML =
            ".homepage-map__mobile-container .interactive-map div div a[target='_blank'] img[src*='google_white'] {top:-310px !important;} ";
        document.head.appendChild(css);
    }, []);

    return (
        <ProtestMapErrorBoundary>
            <div
                className={`homepage-map 
                    eds-l-mar-top-8
                    eds-l-sn-mar-bot-4
                    eds-l-sm-mar-bot-4
                    eds-l-sw-mar-bot-4
                    eds-l-mn-mar-bot-4
                     ${
                         shouldOpenDownwards ? 'homepage-map--marker-down' : ''
                     } ${
                    shouldOpenRightwards ? 'homepage-map--marker-right' : ''
                }`}
            >
                <div
                    ref={desktopRef}
                    className="homepage-map__desktop-container eds-show-up-md"
                >
                    <MapBannerDesktop />
                    <div className="homepage-map__gray-rect" />
                    <MapBase
                        isMobile={false}
                        collectionsResponse={collectionsResponse}
                        handleMapMarkerClick={handleMapMarkerClick}
                        isQA={isQA}
                        parentRef={desktopRef}
                        hasError={hasError}
                        errorState={<ErrorStateDesktop />}
                        refreshPlaceholder={
                            <div className="homepage-map__desktop-placeholder">
                                <a className="eds-align--center" href="/">
                                    Loading... (click here if you just resized
                                    your screen)
                                </a>
                            </div>
                        }
                    />
                </div>
                <div
                    ref={mobileRef}
                    className="homepage-map__mobile-container eds-show-down-mn"
                >
                    <MapBannerMobileTablet />
                    <MapBase
                        isMobile={true}
                        collectionsResponse={collectionsResponse}
                        handleMapMarkerClick={handleMapMarkerClick}
                        isQA={isQA}
                        parentRef={mobileRef}
                        hasError={hasError}
                        errorState={<ErrorStateMobile />}
                        refreshPlaceholder={
                            <div className="homepage-map__mobile-placeholder">
                                {hasError ? (
                                    <a
                                        className="eds-align--center"
                                        href={PROTEST_COLLECTION_URL}
                                    >
                                        Error loading events. Click here to view
                                        them all.
                                    </a>
                                ) : (
                                    <a className="eds-align--center" href="/">
                                        Loading... (click here if you just
                                        resized your screen)
                                    </a>
                                )}
                            </div>
                        }
                    />
                    <div className="homepage-map__gray-rect--mobile">
                        {/* 
                            Use the banner as a way for the gray rectangle to "know" the height
                            of the banner, since we want it to adjust its height WITH the height of the 
                            banner across viewport sizes
                        */}
                        <MapBannerMobileTablet />
                    </div>
                </div>
                <MapMarkerTooltip
                    selectedEvents={events.filter(
                        ({ id }) => id === selectedEventId,
                    )}
                    numEvents={events.length}
                    // Likes button is hidden, no time to implement that
                    onSaveEvent={() => null}
                    onClickOutside={() => setSelectedEventId(null)}
                    parentIsFixed={false}
                    markerRef={mapMarkerRef}
                />
            </div>
        </ProtestMapErrorBoundary>
    );
}
